import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { EventOption, EventOptionGroup, FootballScore, PeriodGameScore, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { MarketType } from '@frontend/sports/betting-offer/feature/types';
import { MarqueeConfig, OddsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { MarketType as MarketTypeEnum } from '@frontend/sports/types/components/content';

import { OptionGroupService } from '../event-model/helpers/option-group.service';
import { UrlHelperService } from '../navigation-core/url-helper.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { HighlightsMarqueePageService } from './highlights-marquee-page.service';
import { HighlightsRegularCardComponent } from './highlights-regular-card.component';
import { MarqueeHelperService } from './marquee-helper.service';
import { ScoreDetails, Tile } from './marquee-tile.model';
import { MarqueeTrackingService } from './marquee-tracking.service';
import { MarqueeConstants } from './marquee.constants';

@Component({
    selector: 'ms-correct-score-card',
    templateUrl: 'correct-score-card.html',
    styleUrls: ['correct-score-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CorrectScoreCardComponent extends HighlightsRegularCardComponent implements OnInit, OnChanges {
    @Input() backgroundImage: string;

    @Output() correctScoreFallbacktoRegularTile = new EventEmitter<{ tile: Tile; marketType: string }>();

    @HostBinding('class') className = 'correct-score-card';

    participant1LiveScore = 0;
    participant2LiveScore = 0;
    participant1Score = 0;
    participant2Score = 0;
    participant1Name: string;
    participant2Name: string;
    override marketOverridenName: string | undefined;
    marketType: MarketType;
    disableOdds = false;
    constructor(
        protected override urlHelper: UrlHelperService,
        protected override navigationRouter: NavigationRouterService,
        protected override optionGroupService: OptionGroupService,
        protected override marqueeConstants: MarqueeConstants,
        protected override sitecore: Sitecore,
        protected override oddsConfig: OddsConfig,
        protected override offerGroupingService: OfferGroupingService,
        protected override highlightsMarqueePageService: HighlightsMarqueePageService,
        protected override marqueeConfig: MarqueeConfig,
        protected override marqueeHelperService: MarqueeHelperService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected override router: Router,
        protected override marqueeTrackingService: MarqueeTrackingService,
    ) {
        super(
            urlHelper,
            navigationRouter,
            optionGroupService,
            marqueeConstants,
            sitecore,
            oddsConfig,
            offerGroupingService,
            highlightsMarqueePageService,
            marqueeConfig,
            marqueeHelperService,
            router,
            marqueeTrackingService,
            changeDetectorRef,
        );
    }

    override ngOnChanges(changes: ISimpleChanges<CorrectScoreCardComponent>): void {
        if (changes.tile) {
            if (changes.tile.firstChange) {
                this.url = !this.tile.isFromPortal ? this.urlHelper.getEventUrl(this.tile.event) : '';
            }

            const currentParticipantScores = this.getCurrentScores();

            this.participant1LiveScore = currentParticipantScores[0];
            this.participant2LiveScore = currentParticipantScores[1];

            // keep previous selected score if available in order to display the same market to the user.
            const participant1Score = this.tile.event.optionGroups
                .find(
                    (optionGroup) =>
                        (this.tile.marketType === MarketType.CorrectScore || MarketType.DynamicCorrectScore) &&
                        this.tile.sportId === SportConstant.Icehockey,
                )
                ?.options[0]?.name.split('-')[0];

            const participant2Score = this.tile.event.optionGroups
                .find(
                    (optionGroup) =>
                        (this.tile.marketType === MarketType.CorrectScore || MarketType.DynamicCorrectScore) &&
                        this.tile.sportId === SportConstant.Icehockey,
                )
                ?.options[0]?.name.split('-')[1];

            if (participant1Score) {
                this.participant1Score = +participant1Score;
            }
            if (participant2Score) {
                this.participant2Score = +participant2Score;
            }

            this.participant1Score = this.participant1Score || currentParticipantScores[0];
            this.participant2Score = this.participant2Score || currentParticipantScores[1];

            this.tileOptionGroup = this.getCurrentOptionGroup(this.participant1Score, this.participant2Score);
            this.updateComponentsData();
            const marketOverride = this.marqueeConfig.marqueeMarketOverrides[this.tile.marketType!.toLowerCase()];

            this.marketOverridenName = marketOverride || this.tileOptionGroup?.name;
            this.tileClass = this.getTileClass();
            this.truncationPlayer = this.getTruncationPlayer();
            this.participant1Name = this.tile.event?.participants[0]?.shortName || this.tile.event?.participants[0]?.name;
            this.participant2Name = this.tile.event?.participants[1]?.shortName || this.tile.event?.participants[1]?.name;
        }
    }

    private getCurrentOptionGroup(p1Score: number, p2Score: number): EventOptionGroup | undefined {
        const market = `${p1Score}:${p2Score}`;
        const marketDash = `${p1Score}-${p2Score}`;
        const optionGroups = this.tile.event.optionGroups.find(
            (optionGroup) =>
                optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.CorrectScore ||
                optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.DynamicCorrectScore,
        );

        const options = optionGroups?.options;

        let filteredOption = options?.find((option) => option.name === market || option.name === marketDash);
        if (!filteredOption) {
            filteredOption =
                this.tile.sportId === SportConstant.Icehockey && p1Score === p2Score
                    ? new EventOption()
                    : (options?.find((option) => !option.name.includes(':') && !option.name.includes('-')) ?? new EventOption());
        }

        const tileOptionGroup = Object.assign(new EventOptionGroup(), optionGroups);
        if (this.marqueeConfig.isFallbackMarqueeEnabled && this.isCorrectScoreToRegularTile(tileOptionGroup)) {
            this.correctScoreFallbacktoRegularTile.emit({
                tile: this.tile,
                marketType: this.marketType,
            });
        }
        if (filteredOption) {
            tileOptionGroup.options = [filteredOption];
        }
        this.disableOdds = this.tile.sportId === SportConstant.Icehockey && p1Score === p2Score;

        return tileOptionGroup;
    }

    private updateComponentsData() {
        this.marqueeOptionGroupComponent?.update({
            optionGroup: this.tileOptionGroup,
            event: this.tile.event,
        });

        this.betNowButtonComponent?.update({
            event: this.tile.event,
        });
    }

    changeScoreEvent(scoreDetails: ScoreDetails): void {
        if (scoreDetails.position === 0) {
            this.participant1Score = scoreDetails.score;
        } else {
            this.participant2Score = scoreDetails.score;
        }
        this.tileOptionGroup = this.getCurrentOptionGroup(this.participant1Score, this.participant2Score);
        this.marqueeOptionGroupComponent?.update({
            optionGroup: this.tileOptionGroup,
        });
        this.changeDetectorRef.markForCheck();
    }

    preventAction(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    private isCorrectScoreToRegularTile(optionGroup: EventOptionGroup | undefined): boolean {
        if (
            (!optionGroup?.options || (optionGroup?.options && this.marqueeHelperService.checkOddsThreshold(optionGroup))) &&
            this.tile.fallbackMarketType !== MarketTypeEnum.Unknown &&
            !this.tile.source?.isAutomatedMarquee
        ) {
            this.marketType = this.tile.fallbackMarketType as MarketType;

            return true;
        }

        return false;
    }

    private getCurrentScores(): number[] {
        switch (this.tile.sportId) {
            case SportConstant.Soccer: {
                const score = this.tile.event.scoreboard.getScore<FootballScore>();

                return [score?.participant1 ?? 0, score?.participant2 ?? 0];
            }
            case SportConstant.Icehockey: {
                const score = this.tile.event.scoreboard.getScore<PeriodGameScore>();

                return [score?.participant1?.total ?? 0, score?.participant2?.total ?? 0];
            }
        }

        return [0, 0];
    }
}
